import { useCallback } from "react";
import { Alert, Button, Modal } from "react-bootstrap";
import type { BillingController } from "../../../api/src/billing/billing.controller";
import type { UnpackResponse } from "../../../api/src/lib";
import SubmitButton from "../components/SubmitButton";
import { useAuthenticatedMutationNew } from "../lib/api";

type FinalizeConfirmationProps = {
  /** Whether the confirmation is in front of the advisor. */
  show: boolean;
  /** The billing report the advisor pressed Finalize on. */
  reportId: number;
  /**
   * Close the confirmation without finalizing. Cancel, the close button, the
   * backdrop and Escape all take this route, and none of them touches the
   * report: no status change, and no finalize workflow started.
   */
  onHide: () => void;
  /**
   * Called once the finalize request has succeeded, so the surface that
   * rendered this can refresh what it shows. The report is finalized by then;
   * the workflow's steps run on after the request returns (R30).
   */
  onFinalized?: () => void;
};

/**
 * The confirmation both finalize surfaces render — the reports list and the
 * report page — so the advisor reads the same words whichever one they pressed
 * Finalize on.
 *
 * It states one consequence before they confirm: Slant has no note DELETE, so
 * the note a finalize publishes stays on the household's record. Everything
 * else about finalizing can be recovered from by running a corrected billing
 * report and finalizing that; this cannot
 * (s-billing-report-finalization-capability-019fba175c3d304d, R26 / AC26,
 * §Recovery).
 */
const FinalizeConfirmation = ({
  show,
  reportId,
  onHide,
  onFinalized,
}: FinalizeConfirmationProps) => {
  const finalize = useAuthenticatedMutationNew<
    UnpackResponse<BillingController["finalizeBillingReport"]>,
    void,
    void
  >({
    mutationKey: ["POST", "v1", "billing", "reports", reportId, "finalization"],
    mutationFn: () => undefined,
  });

  const { mutate } = finalize;

  const onConfirm = useCallback(() => {
    mutate(undefined, {
      onSuccess: () => {
        onFinalized?.();
        onHide();
      },
    });
  }, [mutate, onFinalized, onHide]);

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton={!finalize.isPending}>
        <Modal.Title>Finalize Billing Report</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/*
          A failed request leaves the report as it was, so the confirmation
          stays open and says so rather than closing as though it had worked.
        */}
        {!finalize.isError ? null : (
          <Alert variant="danger">
            The report could not be finalized. It is unchanged.
          </Alert>
        )}
        <p>
          Finalizing publishes a note onto each household&apos;s Slant record,
          and that note cannot afterwards be removed.
        </p>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onHide}
          disabled={finalize.isPending}
        >
          Cancel
        </Button>
        <SubmitButton
          label="Finalize"
          variant="danger"
          onClick={onConfirm}
          isSubmitting={finalize.isPending}
        />
      </Modal.Footer>
    </Modal>
  );
};

export default FinalizeConfirmation;
